import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface AnalyticsData {
  users: number;
  properties: number;
  leads: number;
  deals: number;
  pipeline_value: number;
  revenue: number;
  pending_reminders: number;
  media: number;
}

export default function AnalyticsTab() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get<{ stats: AnalyticsData }>("/dashboard");
      setData(res.stats);
    } catch (e: any) {
      setError(e?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = [
    { label: "Users", value: data?.users ?? 0, icon: "ri-user-line", color: "bg-primary-100 text-primary-700" },
    { label: "Projects", value: data?.properties ?? 0, icon: "ri-building-line", color: "bg-accent-100 text-accent-700" },
    { label: "Leads", value: data?.leads ?? 0, icon: "ri-contacts-line", color: "bg-secondary-100 text-secondary-700" },
    { label: "Deals", value: data?.deals ?? 0, icon: "ri-hand-coin-line", color: "bg-primary-100 text-primary-700" },
    { label: "Revenue", value: data?.revenue ?? 0, icon: "ri-money-rupee-circle-line", color: "bg-accent-100 text-accent-700", prefix: "₹" },
    { label: "Pipeline", value: data?.pipeline_value ?? 0, icon: "ri-funds-line", color: "bg-secondary-100 text-secondary-700", prefix: "₹" },
    { label: "Reminders", value: data?.pending_reminders ?? 0, icon: "ri-time-line", color: "bg-primary-100 text-primary-700" },
    { label: "Media", value: data?.media ?? 0, icon: "ri-image-line", color: "bg-accent-100 text-accent-700" },
  ];

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">
          {error}
        </div>
      )}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-100 text-primary-700">
              <i className="ri-bar-chart-box-line"></i>
            </span>
            <div>
              <h3 className="font-heading text-base text-foreground-950">Site Analytics</h3>
              <p className="text-xs text-foreground-600">Overview of your site activity and content.</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {loading ? (
            <p className="text-center text-sm text-foreground-600 py-8">
              <i className="ri-loader-4-line animate-spin"></i> Loading...
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl bg-background-100 border border-background-200 p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg ${s.color}`}>
                      <i className={s.icon}></i>
                    </span>
                    <span className="text-xs text-foreground-600">{s.label}</span>
                  </div>
                  <p className="text-xl font-bold text-foreground-950">
                    {s.prefix || ""}
                    {s.value.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}